import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { DualPaneLayout } from '../components/DualPaneLayout';
import { Button } from '../components/design-system/Button';
import { Input } from '../components/design-system/Input';
import { apiService } from '../services/api';
import { getCurrentApiConfig } from '../config/api.config';
import { colors } from '../theme/colors';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface WhatsAppTestResult {
  endpoint: string;
  status: 'pending' | 'success' | 'error';
  response?: any;
  error?: string;
  timestamp: string;
}

export const WhatsAppTestScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const [isLoading, setIsLoading] = useState(false);
  const [testResults, setTestResults] = useState<WhatsAppTestResult[]>([]);
  const [phoneNumber, setPhoneNumber] = useState('+201023356631');
  const [message, setMessage] = useState('Test message from DISLIX');
  const [apiBaseUrl, setApiBaseUrl] = useState(() => {
    const config = getCurrentApiConfig();
    return config?.baseURL || 'https://api-dev.sendifier.com';
  });
  const [useNgrok, setUseNgrok] = useState(false);

  const addTestResult = (result: WhatsAppTestResult) => {
    setTestResults(prev => [result, ...prev]);
  };

  const testWhatsAppEndpoint = async (endpoint: string, method: string = 'GET', body?: any) => {
    const result: WhatsAppTestResult = {
      endpoint,
      status: 'pending',
      timestamp: new Date().toISOString()
    };

    addTestResult(result);

    try {
      const response = await fetch(`${apiBaseUrl}${endpoint}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      const responseData = await response.json();

      if (response.ok) {
        addTestResult({
          ...result,
          status: 'success',
          response: responseData
        });
      } else {
        addTestResult({
          ...result,
          status: 'error',
          error: `HTTP ${response.status}: ${responseData.message || 'Unknown error'}`
        });
      }
    } catch (error: any) {
      addTestResult({
        ...result,
        status: 'error',
        error: error.message || 'Network error'
      });
    }
  };

  const runAllTests = async () => {
    setIsLoading(true);
    setTestResults([]);

    // Test basic connectivity
    await testWhatsAppEndpoint('/api/whatsapp/health', 'GET');
    
    // Test webhook verification
    await testWhatsAppEndpoint('/api/whatsapp/webhook', 'POST', {
      hub_mode: 'subscribe',
      hub_verify_token: 'test_token',
      hub_challenge: 'test_challenge'
    });

    // Test the new test endpoint
    await testWhatsAppEndpoint('/api/whatsapp/test', 'POST', {
      to: phoneNumber,
      message: message
    });

    // Test sending message
    await testWhatsAppEndpoint('/api/whatsapp/send-message', 'POST', {
      to: phoneNumber,
      type: 'text',
      content: message
    });

    // Test getting templates
    await testWhatsAppEndpoint('/api/whatsapp/templates', 'GET');

    // Test getting configs
    await testWhatsAppEndpoint('/api/whatsapp/configs', 'GET');

    setIsLoading(false);
  };

  const testSpecificEndpoint = async () => {
    if (!phoneNumber.trim()) {
      Alert.alert('Error', 'Please enter a phone number');
      return;
    }

    setIsLoading(true);
    await testWhatsAppEndpoint('/api/whatsapp/send-message', 'POST', {
      to: phoneNumber,
      type: 'text',
      content: message
    });
    setIsLoading(false);
  };

  const clearResults = () => {
    setTestResults([]);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return colors.success;
      case 'error': return colors.error;
      case 'pending': return colors.warning;
      default: return colors.textDark;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'pending': return '⏳';
      default: return '❓';
    }
  };

  const toggleNgrok = () => {
    if (useNgrok) {
      setApiBaseUrl('http://localhost:4000');
    } else {
      const config = getCurrentApiConfig();
      setApiBaseUrl(config?.baseURL || 'https://api-dev.sendifier.com');
    }
    setUseNgrok(!useNgrok);
  };

  return (
    <DualPaneLayout title="DISLIX" subtitle="WhatsApp Connectivity Test">
      <SafeAreaView style={styles.container}>
        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          
          {/* Test Configuration */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Test Configuration</Text>
            
            <Input
              label="API Base URL"
              placeholder="http://localhost:4000 or https://api-dev.sendifier.com"
              value={apiBaseUrl}
              onChangeText={setApiBaseUrl}
              size="md"
            />

            <View style={styles.ngrokToggle}>
              <Text style={styles.ngrokLabel}>Use ngrok for HTTPS testing:</Text>
              <TouchableOpacity 
                style={[styles.toggleButton, { backgroundColor: useNgrok ? colors.success : colors.lightGray }]}
                onPress={toggleNgrok}
              >
                <Text style={styles.toggleText}>{useNgrok ? 'ON' : 'OFF'}</Text>
              </TouchableOpacity>
            </View>

            <Input
              label="Phone Number"
              placeholder="+1234567890"
              value={phoneNumber}
              onChangeText={setPhoneNumber}
              size="md"
            />

            <Input
              label="Test Message"
              placeholder="Test message content"
              value={message}
              onChangeText={setMessage}
              size="md"
              multiline
            />

            <View style={styles.buttonRow}>
              <Button
                title="Run All Tests"
                onPress={runAllTests}
                disabled={isLoading}
                size="md"
                variant="primary"
                style={styles.button}
              />
              
              <Button
                title="Test Send Message"
                onPress={testSpecificEndpoint}
                disabled={isLoading}
                size="md"
                variant="secondary"
                style={styles.button}
              />
            </View>

            <Button
              title="Clear Results"
              onPress={clearResults}
              size="sm"
              variant="outline"
              style={styles.clearButton}
            />
          </View>

          {/* Test Results */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              Test Results ({testResults.length})
            </Text>

            {testResults.length === 0 ? (
              <Text style={styles.noResults}>No tests run yet. Click "Run All Tests" to start.</Text>
            ) : (
              testResults.map((result, index) => (
                <View key={index} style={styles.resultItem}>
                  <View style={styles.resultHeader}>
                    <Text style={styles.resultEndpoint}>{result.endpoint}</Text>
                    <View style={styles.resultStatus}>
                      <Text style={[styles.statusText, { color: getStatusColor(result.status) }]}>
                        {getStatusIcon(result.status)} {result.status.toUpperCase()}
                      </Text>
                      <Text style={styles.timestamp}>
                        {new Date(result.timestamp).toLocaleTimeString()}
                      </Text>
                    </View>
                  </View>

                  {result.error && (
                    <Text style={styles.errorText}>Error: {result.error}</Text>
                  )}

                  {result.response && (
                    <View style={styles.responseContainer}>
                      <Text style={styles.responseLabel}>Response:</Text>
                      <Text style={styles.responseText}>
                        {JSON.stringify(result.response, null, 2)}
                      </Text>
                    </View>
                  )}
                </View>
              ))
            )}
          </View>

          {/* Connection Info */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Connection Information</Text>
            <Text style={styles.infoText}>API Base: {apiBaseUrl}</Text>
            <Text style={styles.infoText}>WhatsApp Endpoint: /api/whatsapp</Text>
            <Text style={styles.infoText}>Status: {isLoading ? 'Testing...' : 'Ready'}</Text>
            <Text style={styles.infoText}>Mode: {useNgrok ? 'ngrok HTTPS' : 'Local HTTP'}</Text>
          </View>

          {/* Setup Instructions */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Setup Instructions</Text>
            <Text style={styles.infoText}>1. Start your local server: node lib/main.js</Text>
            <Text style={styles.infoText}>2. For HTTPS testing, use ngrok: ngrok http 4000</Text>
            <Text style={styles.infoText}>3. Current API URL: {apiBaseUrl}</Text>
            <Text style={styles.infoText}>4. Environment: {getCurrentApiConfig()?.baseURL?.includes('api-tunnel') ? 'Local Development (api-tunnel)' : 'Release Build (api-dev)'}</Text>
            <Text style={styles.infoText}>5. Use this URL in Facebook webhook configuration</Text>
            <Text style={styles.infoText}>6. Webhook URL: /api/whatsapp/webhook</Text>
            <Text style={styles.infoText}>7. Verify Token: test_token (for testing)</Text>
          </View>

        </ScrollView>
      </SafeAreaView>
    </DualPaneLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  section: {
    marginBottom: 30,
    backgroundColor: colors.background.surface,
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: 15,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 15,
  },
  button: {
    flex: 1,
  },
  clearButton: {
    alignSelf: 'center',
  },
  noResults: {
    textAlign: 'center',
    color: colors.textLight,
    fontStyle: 'italic',
    padding: 20,
  },
  resultItem: {
    backgroundColor: colors.background.primary,
    padding: 15,
    borderRadius: 8,
    marginBottom: 10,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
  },
  resultHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  resultEndpoint: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.textDark,
    flex: 1,
  },
  resultStatus: {
    alignItems: 'flex-end',
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 2,
  },
  timestamp: {
    fontSize: 10,
    color: colors.textLight,
  },
  errorText: {
    color: colors.error,
    fontSize: 12,
    marginBottom: 10,
  },
  responseContainer: {
    backgroundColor: colors.background.primary,
    padding: 10,
    borderRadius: 6,
  },
  responseLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: colors.textDark,
    marginBottom: 5,
  },
  responseText: {
    fontSize: 10,
    color: colors.textLight,
    fontFamily: 'monospace',
  },
  infoText: {
    fontSize: 14,
    color: colors.textLight,
    marginBottom: 5,
  },
  ngrokToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 15,
    paddingVertical: 10,
    paddingHorizontal: 15,
    backgroundColor: colors.lightGray,
    borderRadius: 8,
  },
  ngrokLabel: {
    fontSize: 14,
    color: colors.textDark,
  },
  toggleButton: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 5,
  },
  toggleText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.white,
  },
});
